import { CartLinesUpdateEvent } from '@shopify/events';

// Même clé que le carrousel produits : l'état des favoris est partagé.
const WISHLIST_KEY = 'cpc:wishlist';

/** @returns {Set<string>} */
function readWishlist() {
  try {
    const raw = localStorage.getItem(WISHLIST_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

/** @param {Set<string>} ids */
function writeWishlist(ids) {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
}

/** @param {number} cents */
function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  const symbol = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
  return symbol ? `${amount} ${symbol}` : amount;
}

/**
 * Section « Produit + collection » : favoris locaux, et quick add qui ouvre
 * un drawer bas pour choisir taille / options avant l'ajout au panier.
 */
class CustomFeaturedSplit extends HTMLElement {
  #abort = new AbortController();

  /** @type {any} */ #product = null;
  /** @type {(string|null)[]} */ #selected = [];
  /** @type {any} */ #variant = null;

  connectedCallback() {
    const { signal } = this.#abort;
    this.drawer = this.querySelector('[data-cfs-drawer]');
    this.#initWishlist(signal);
    this.#initQuickAdd(signal);
    this.#initDrawer(signal);
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /* ---------- Favoris ---------- */

  #initWishlist(signal) {
    const saved = readWishlist();

    for (const button of this.querySelectorAll('.cfs__wishlist')) {
      const productId = button.dataset.productId;
      if (!productId) continue;
      if (saved.has(productId)) button.setAttribute('aria-pressed', 'true');

      button.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          const ids = readWishlist();
          const wasSaved = ids.has(productId);
          if (wasSaved) ids.delete(productId);
          else ids.add(productId);
          writeWishlist(ids);
          button.setAttribute('aria-pressed', String(!wasSaved));
        },
        { signal }
      );
    }
  }

  /* ---------- Quick add ---------- */

  #initQuickAdd(signal) {
    for (const button of this.querySelectorAll('.cfs__quick-add')) {
      button.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();

          // Produit à variante unique : ajout direct, sans drawer.
          if (Number(button.dataset.variantCount) <= 1) {
            this.#addToCart(button.dataset.variantId, button);
            return;
          }
          this.#openDrawer(button.dataset.productUrl);
        },
        { signal }
      );
    }
  }

  /* ---------- Drawer ---------- */

  #initDrawer(signal) {
    if (!this.drawer) return;

    for (const el of this.drawer.querySelectorAll('[data-cfs-close]')) {
      el.addEventListener('click', () => this.#closeDrawer(), { signal });
    }

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && !this.drawer.hidden) this.#closeDrawer();
      },
      { signal }
    );

    this.drawer.querySelector('.cfs__drawer-add')?.addEventListener(
      'click',
      () => {
        if (this.#variant) this.#addToCart(String(this.#variant.id), null, () => this.#closeDrawer());
      },
      { signal }
    );
  }

  async #openDrawer(url) {
    if (!this.drawer || !url) return;

    try {
      const product = await fetch(`${url}.js`).then((r) => r.json());
      this.#product = product;
      this.#selected = product.options.map(() => null);
      this.#variant = null;
      this.#renderDrawer();
      this.#showDrawer();
    } catch (error) {
      console.error('[custom-featured-split] chargement produit impossible', error);
    }
  }

  #renderDrawer() {
    const product = this.#product;
    const drawer = this.drawer;
    drawer.querySelector('.cfs__drawer-title').textContent = product.title;
    drawer.querySelector('.cfs__drawer-price').textContent = formatMoney(product.price);

    const names = product.options.map((o) => (typeof o === 'string' ? o : o.name));
    const optionsEl = drawer.querySelector('.cfs__drawer-options');
    optionsEl.innerHTML = '';

    names.forEach((name, index) => {
      const values = [...new Set(product.variants.map((v) => v.options[index]))];
      const group = document.createElement('div');
      group.className = 'cfs__opt';
      const label = document.createElement('span');
      label.className = 'cfs__opt-name';
      label.textContent = name;
      const row = document.createElement('div');
      row.className = 'cfs__opt-values';

      values.forEach((value) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cfs__opt-value';
        btn.textContent = value;
        btn.dataset.index = String(index);
        btn.dataset.value = value;
        btn.setAttribute('aria-pressed', 'false');
        btn.addEventListener('click', () => this.#selectValue(index, value));
        row.appendChild(btn);
      });

      group.append(label, row);
      optionsEl.appendChild(group);
    });

    this.#refreshOptionStates();
    this.#updateVariant();
  }

  #selectValue(index, value) {
    this.#selected[index] = this.#selected[index] === value ? null : value;
    this.#refreshOptionStates();
    this.#updateVariant();
  }

  /** Grise les valeurs qui ne mènent à aucune variante disponible. */
  #refreshOptionStates() {
    const product = this.#product;
    for (const btn of this.drawer.querySelectorAll('.cfs__opt-value')) {
      const index = Number(btn.dataset.index);
      const value = btn.dataset.value;
      btn.setAttribute('aria-pressed', String(this.#selected[index] === value));

      const possible = product.variants.some((v) => {
        if (!v.available) return false;
        if (v.options[index] !== value) return false;
        return this.#selected.every((sel, i) => i === index || sel === null || v.options[i] === sel);
      });
      btn.disabled = !possible;
    }
  }

  #updateVariant() {
    const product = this.#product;
    const addBtn = this.drawer.querySelector('.cfs__drawer-add');
    const complete = this.#selected.every((s) => s !== null);

    this.#variant = complete
      ? product.variants.find((v) => this.#selected.every((sel, i) => v.options[i] === sel))
      : null;

    if (this.#variant) {
      this.drawer.querySelector('.cfs__drawer-price').textContent = formatMoney(this.#variant.price);
    }

    const ok = Boolean(this.#variant && this.#variant.available);
    addBtn.disabled = !ok;
    addBtn.textContent = ok
      ? this.dataset.addLabel || 'Ajouter au panier'
      : this.dataset.chooseLabel || 'Choisir les options';
  }

  #showDrawer() {
    this.drawer.hidden = false;
    // Force un reflow avant d'ajouter la classe pour jouer la transition.
    void this.drawer.offsetWidth;
    this.drawer.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
  }

  #closeDrawer() {
    this.drawer.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    const panel = this.drawer.querySelector('.cfs__drawer-panel');
    const done = () => {
      this.drawer.hidden = true;
      panel.removeEventListener('transitionend', done);
    };
    panel.addEventListener('transitionend', done);
  }

  /* ---------- Ajout panier ---------- */

  async #addToCart(variantId, button, onDone) {
    if (!variantId) return;
    if (button) button.disabled = true;

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      }).then((r) => r.json());

      if (response.status) {
        throw new Error(response.description || response.message || 'Add to cart failed');
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'custom-featured-split',
          sourceId: this.id,
          itemCount: 1,
          didError: false,
        },
      });
      if (onDone) onDone();
    } catch (error) {
      console.error('[custom-featured-split] ajout au panier impossible', error);
      deferred.reject(error);
    } finally {
      if (button) button.disabled = false;
    }
  }
}

if (!customElements.get('custom-featured-split')) {
  customElements.define('custom-featured-split', CustomFeaturedSplit);
}
